import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Home, MapPin, Maximize2, BedDouble, MessageCircle, X, Map as MapIcon } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import PageTransition from "../components/PageTransition";
import Lightbox from "../components/Lightbox";

const HARAMS = [
    { name: "Kâbe (Mescid-i Haram)", short: "Harem", lat: 21.4225, lng: 39.8262 },
    { name: "Mescid-i Nebevî", short: "Mescid-i Nebevî", lat: 24.4672, lng: 39.6111 },
];

const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371, rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
};

const nearestHaram = (p) => {
    if (p.latitude == null || p.longitude == null) return null;
    const dists = HARAMS.map((h) => ({ ...h, km: haversineKm(p.latitude, p.longitude, h.lat, h.lng) }));
    const best = dists.sort((a, b) => a.km - b.km)[0];
    return best.km <= 100 ? best : null;
};

const fmtDist = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1).replace(".", ",")} km`);

const pinIcon = (color) => L.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
});

const PropertyMapModal = ({ prop, onClose }) => {
    const haram = nearestHaram(prop);
    const center = [prop.latitude, prop.longitude];
    return (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="property-map-modal">
            <div className="bg-emerald_surface border border-border_green w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-border_green">
                    <div>
                        <div className="font-serif text-lg text-bone">{prop.title}</div>
                        {haram && <div className="text-xs text-gold">{haram.short}'e {fmtDist(haram.km)}</div>}
                    </div>
                    <button onClick={onClose} className="text-muted_green hover:text-bone p-2" data-testid="property-map-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="h-[420px]">
                    <MapContainer center={center} zoom={haram && haram.km < 8 ? 13 : 11} className="w-full h-full" scrollWheelZoom>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                        <Marker position={center} icon={pinIcon("#d4af37")}>
                            <Popup>{prop.title}</Popup>
                        </Marker>
                        {haram && (
                            <>
                                <Marker position={[haram.lat, haram.lng]} icon={pinIcon("#0f6b4f")}>
                                    <Popup>{haram.name}</Popup>
                                </Marker>
                                <Polyline positions={[center, [haram.lat, haram.lng]]} pathOptions={{ color: "#d4af37", dashArray: "6 8", weight: 2 }} />
                            </>
                        )}
                    </MapContainer>
                </div>
            </div>
        </div>
    );
};

const PropertyCard = ({ p, settings, onOpenGallery, onOpenMap }) => {
    const wa = (settings.whatsapp_number || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Merhaba, "${p.title}" ilanı hakkında bilgi almak istiyorum.`);
    const img = (p.images || [])[0];
    const haram = nearestHaram(p);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
            className="bg-emerald_surface border border-border_green overflow-hidden flex flex-col"
            data-testid={`property-card-${p.id}`}
        >
            <div
                className="aspect-[16/10] bg-emerald_deep relative overflow-hidden cursor-pointer"
                onClick={() => (p.images || []).length > 0 && onOpenGallery(p)}
            >
                {img ? (
                    <img src={resolveImageUrl(img)} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted_green">
                        <Home className="w-16 h-16" />
                    </div>
                )}
                <div className="absolute top-3 left-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-gold border border-gold/40">
                    {p.listing_type === "rent" ? "Kiralık" : "Satılık"}
                </div>
                {haram && (
                    <div className="absolute top-3 right-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-bone border border-gold/40 flex items-center gap-1" data-testid={`haram-distance-${p.id}`}>
                        <MapPin className="w-3 h-3 text-gold" /> {haram.short}'e {fmtDist(haram.km)}
                    </div>
                )}
                {(p.images || []).length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] text-bone/90">
                        {p.images.length} fotoğraf
                    </div>
                )}
            </div>

            <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-serif text-xl text-bone leading-tight">{p.title}</h3>
                {p.city && (
                    <div className="flex items-center gap-1.5 text-xs text-muted_green mt-1.5">
                        <MapPin className="w-3.5 h-3.5 text-gold" /> {p.city}
                    </div>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-muted_green">
                    {p.rooms && <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5 text-gold" /> {p.rooms}</span>}
                    {Number(p.area_sqm) > 0 && <span className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5 text-gold" /> {p.area_sqm} m²</span>}
                    {p.latitude != null && p.longitude != null && (
                        <button onClick={() => onOpenMap(p)} className="flex items-center gap-1 text-gold hover:underline" data-testid={`property-map-btn-${p.id}`}>
                            <MapIcon className="w-3.5 h-3.5" /> Haritada Gör
                        </button>
                    )}
                </div>
                {p.description && <p className="text-sm text-bone/80 mt-3 leading-relaxed line-clamp-3">{p.description}</p>}
                {(p.features || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                        {(p.features || []).slice(0, 6).map((f, i) => (
                            <span key={i} className="text-[10px] uppercase tracking-[0.15em] text-muted_green bg-emerald_deep/40 border border-border_green px-2 py-0.5">{f}</span>
                        ))}
                    </div>
                )}

                <div className="mt-auto pt-5 flex items-end justify-between gap-3 border-t border-border_green mt-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted_green">{p.listing_type === "rent" ? "Aylık" : "Fiyat"}</div>
                        <div className="text-gold font-serif text-2xl">
                            {Number(p.price) > 0 ? `${Number(p.price).toLocaleString("tr-TR")} ${p.currency || "SAR"}` : "Fiyat için sorunuz"}
                        </div>
                    </div>
                    {wa && (
                        <a href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noreferrer" className="btn-gold !text-xs" data-testid={`property-wa-${p.id}`}>
                            <MessageCircle className="w-3.5 h-3.5" /> Bilgi Al
                        </a>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default function RealEstate() {
    const { settings } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [gallery, setGallery] = useState(null); // {images, index}
    const [mapProp, setMapProp] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/properties").then(({ data }) => { if (alive) setItems(data || []); }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const filtered = filter === "all" ? items : items.filter((p) => p.listing_type === filter);

    return (
        <PageTransition>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="realestate-page">
                <div className="text-center mb-10">
                    <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">EMLAK</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">Satılık & Kiralık Evler</h1>
                    <p className="text-bone/70 mt-4 max-w-2xl mx-auto leading-relaxed">
                        Mekke, Medine ve Türkiye'de güvenilir emlak fırsatları. Detaylı bilgi için WhatsApp'tan bize ulaşın.
                    </p>
                </div>

                <div className="flex items-center justify-center gap-2 mb-8">
                    {[["all", "Tümü"], ["sale", "Satılık"], ["rent", "Kiralık"]].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${filter === k ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-muted_green hover:border-gold hover:text-gold"}`}
                            data-testid={`property-filter-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-center text-muted_green py-12">Yükleniyor…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-border_green">
                        <Home className="w-12 h-12 text-muted_green mx-auto mb-3" />
                        <div className="text-muted_green">Henüz ilan eklenmemiş. Yakında burada olacak — bilgi için bizimle iletişime geçin.</div>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="property-grid">
                        {filtered.map((p) => (
                            <PropertyCard key={p.id} p={p} settings={settings} onOpenGallery={(prop) => setGallery({ images: prop.images, index: 0 })} onOpenMap={setMapProp} />
                        ))}
                    </div>
                )}

                {mapProp && <PropertyMapModal prop={mapProp} onClose={() => setMapProp(null)} />}

                {gallery && (
                    <Lightbox
                        images={(gallery.images || []).map(resolveImageUrl)}
                        index={gallery.index}
                        onClose={() => setGallery(null)}
                        onChange={(i) => setGallery((g) => ({ ...g, index: i }))}
                    />
                )}
            </section>
        </PageTransition>
    );
}
